import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { actionErrorMessage, isRepliesOff, REPLIES_PAUSED_MESSAGE } from '../../api/client'
import { api } from '../../api/endpoints'
import { usePrivateReplies } from '../../state/privateReplies'
import { toast } from '../../state/toasts'
import { syncAll } from '../../sync/syncEngine'
import { storeNewChannel } from '../channels/createConversation'

/**
 * Opens the caller's conversation with a restricted channel's reply bot and remembers which channel message the
 * next message there answers. Returns the conversation id, or null when it could not be opened (toasted).
 */
export async function openPrivateReply(channelId: string, messageId: string): Promise<string | null> {
  try {
    const conversation = await api.replyConversation(channelId)
    await storeNewChannel(conversation)
    usePrivateReplies.getState().start(conversation.id, { channelId, messageId })
    return conversation.id
  } catch (error) {
    if (isRepliesOff(error)) {
      // This device thought replies were open: its copy of the channel is out of date.
      toast(REPLIES_PAUSED_MESSAGE)
      void syncAll()
    } else {
      toast(actionErrorMessage("Couldn't start a private reply.", error))
    }
    return null
  }
}

/** "Reply privately": open the reply bot conversation with the message attached and go there. */
export function usePrivateReply(): (channelId: string, messageId: string) => Promise<void> {
  const navigate = useNavigate()
  return useCallback(
    async (channelId: string, messageId: string) => {
      const conversationId = await openPrivateReply(channelId, messageId)
      if (conversationId) navigate(`/c/${conversationId}`)
    },
    [navigate],
  )
}
